import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Bell, Home, LogOut, Menu, X, MoonStar, ShieldAlert, MessageSquare } from "lucide-react";
import NotificationSettings from "@/components/NotificationSettings";
import { useContent } from "@/context/ContentContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { c } = useContent();
  const [notifs, setNotifs] = useState([]);
  const [msgUnread, setMsgUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const loadNotifs = () => {
    if (!user) return;
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    api.get("/messages/unread-count").then((r) => setMsgUnread(r.data.count)).catch(() => {});
  };

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user]);

  const unread = notifs.filter((n) => !n.read).length;

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`).catch(() => {});
    loadNotifs();
  };

  const notifRoute = (n) => {
    const base = user.role === "admin" ? "/admin" : "/dashboard";
    const t = `${n.title || ""} ${n.body || ""}`.toLowerCase();
    let tab = null;
    if (t.includes("message")) tab = "messages";
    else if (t.includes("maintenance")) tab = "maintenance";
    else if (t.includes("application")) tab = "applications";
    else if (t.includes("showing")) tab = "showings";
    else if (t.includes("plan") || t.includes("payout") || t.includes("upgrade") || t.includes("platform fee") || t.includes("billing") || t.includes("subscription"))
      tab = user.role === "landlord" ? "payouts" : "leases";
    else if (t.includes("rent") || t.includes("invoice") || t.includes("deposit") || t.includes("lease") || t.includes("document") || t.includes("signed"))
      tab = "leases";
    if (user.role === "admin" && tab !== "messages") tab = null;
    return tab ? `${base}?tab=${tab}` : base;
  };

  const onNotifClick = (n) => {
    markRead(n.id);
    navigate(notifRoute(n));
  };

  return (
    <nav className="bg-white/80 backdrop-blur-xl border-b border-line sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="h-9 w-9 rounded-lg bg-forest flex items-center justify-center">
              <Home className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <span className="font-heading font-semibold text-lg tracking-tight text-ink">
              {c("brand_name")}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/browse"
              data-testid="nav-browse"
              className="px-4 py-2 text-sm font-medium text-mute hover:text-ink rounded-lg hover:bg-bone transition-colors"
            >
              Browse Homes
            </Link>
            {user && (
              <Link
                to="/dashboard"
                data-testid="nav-dashboard"
                className="px-4 py-2 text-sm font-medium text-mute hover:text-ink rounded-lg hover:bg-bone transition-colors"
              >
                Dashboard
              </Link>
            )}
          </div>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <button
                  data-testid="nav-messages"
                  onClick={() => navigate(user.role === "admin" ? "/admin?tab=messages" : "/dashboard?tab=messages")}
                  className="relative h-10 w-10 flex items-center justify-center rounded-lg hover:bg-bone transition-colors"
                  title="Messages"
                >
                  <MessageSquare className="h-5 w-5 text-mute" strokeWidth={1.75} />
                  {msgUnread > 0 && (
                    <span
                      data-testid="nav-messages-count"
                      className="absolute top-1.5 right-1.5 h-4 min-w-4 px-1 rounded-full bg-terracotta text-white text-[10px] font-bold flex items-center justify-center"
                    >
                      {msgUnread}
                    </span>
                  )}
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="nav-notifications"
                      className="relative h-10 w-10 flex items-center justify-center rounded-lg hover:bg-bone transition-colors"
                    >
                      <Bell className="h-5 w-5 text-mute" strokeWidth={1.75} />
                      {unread > 0 && (
                        <span
                          data-testid="notif-count"
                          className="absolute top-1.5 right-1.5 h-4 min-w-4 px-1 rounded-full bg-terracotta text-white text-[10px] font-bold flex items-center justify-center"
                        >
                          {unread}
                        </span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 bg-white border-line rounded-xl shadow-lg p-1">
                    <DropdownMenuLabel className="font-heading">Notifications</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {notifs.length === 0 && (
                      <div className="px-3 py-6 text-center text-sm text-mute">You're all caught up.</div>
                    )}
                    {notifs.slice(0, 8).map((n) => (
                      <DropdownMenuItem
                        key={n.id}
                        data-testid={`notif-item-${n.id}`}
                        onClick={() => onNotifClick(n)}
                        className={`flex flex-col items-start gap-0.5 rounded-md py-2 cursor-pointer ${!n.read ? "bg-bone" : ""}`}
                      >
                        <span className="text-sm font-medium text-ink">{n.title}</span>
                        <span className="text-xs text-mute line-clamp-2">{n.body}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="nav-profile" className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-bone transition-colors">
                      <img src={user.avatar} alt={user.name} className="h-8 w-8 rounded-full" />
                      <span className="hidden sm:block text-sm font-medium text-ink">{user.name}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-white border-line rounded-xl shadow-lg p-1">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{user.name}</span>
                        <span className="text-xs text-mute capitalize">{user.role}</span>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer rounded-md">
                      Dashboard
                    </DropdownMenuItem>
                    {user.role === "admin" && (
                      <DropdownMenuItem data-testid="nav-admin" onClick={() => navigate("/admin")} className="cursor-pointer rounded-md">
                        <ShieldAlert className="h-4 w-4 mr-2" /> Admin Panel
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem data-testid="open-notification-settings" onClick={() => setSettingsOpen(true)} className="cursor-pointer rounded-md">
                      <MoonStar className="h-4 w-4 mr-2" /> Notification settings
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="logout-btn"
                      onClick={() => {
                        logout();
                        navigate("/");
                      }}
                      className="cursor-pointer rounded-md text-red-600"
                    >
                      <LogOut className="h-4 w-4 mr-2" /> Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="px-4 py-2 text-sm font-medium text-ink hover:bg-bone rounded-lg transition-colors"
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-signup"
                  className="px-5 py-2 text-sm font-medium bg-forest text-white rounded-lg hover:bg-forest-hover transition-colors shadow-sm"
                >
                  Get Started
                </Link>
              </>
            )}
            <button data-testid="nav-mobile-menu-btn" className="md:hidden h-10 w-10 flex items-center justify-center" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden py-3 border-t border-line flex flex-col gap-1">
            <Link to="/browse" onClick={() => setMobileOpen(false)} className="px-3 py-2 rounded-lg hover:bg-bone text-sm font-medium">Browse Homes</Link>
            {user && <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="px-3 py-2 rounded-lg hover:bg-bone text-sm font-medium">Dashboard</Link>}
          </div>
        )}
      </div>
      {user && <NotificationSettings open={settingsOpen} onOpenChange={setSettingsOpen} />}
    </nav>
  );
}
